import { useEffect, useMemo, useState } from 'react';

import { toast } from 'sonner';

import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import { useUpdateRelativityFieldMapping } from '@/api/mutations/useUpdateRelativityFieldMapping';
import useGetRelativityFieldMapping from '@/api/queries/useGetRelativityFieldMapping';
import useGetRelativityWorkspaceFields from '@/api/queries/useGetRelativityWorkspaceFields';
import useGetRelativityWorkspaces from '@/api/queries/useGetRelativityWorkspaces';
import type {
  FieldMappings,
  RelativityFieldType,
  RelativityWorkspaceType,
  WexlerStandardFieldType,
  TypeValidationResult,
} from '@/api/types/backend-dto-types';
import { WEXLER_STANDARD_FIELDS, validateTypeCompatibility, TypeCompatibility } from '@/api/types/backend-dto-types';
import Select from '@/components/atoms/Select';
import { useHasPermission } from '@/hooks/useHasPermission';
import { useRelativityAuth } from '@/hooks/useRelativityAuth';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import {
  Select as ShadcnSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/new/components/shadcn/select';
import { FilterOption } from '@/types';
import {
  getValidationIconClass,
  getValidationBorderClass,
  getValidationTextClass,
  getValidationBackgroundClass,
} from '@/utils/relativityTypeCompatibilityUI';

// Convert field name to friendly display name
function fieldToDisplayName(field: WexlerStandardFieldType): string {
  const displayNames: Record<string, string> = {
    controlNumber: 'Control Number',
    fileType: 'File Type',
    fileSize: 'File Size',
    emailFrom: 'Email From',
    emailTo: 'Email To (Array)',
    emailSubject: 'Email Subject',
    emailSentDate: 'Email Sent Date',
    emailReceivedDate: 'Email Received Date',
    documentDate: 'Document Date',
    author: 'Author',
    title: 'Title',
    containerName: 'Container Name',
    containerId: 'Container ID',
    filePath: 'File Path',
    pageCount: 'Page Count',
    issues: 'Issues',
  };
  return displayNames[field] || field;
}

// Check if a field is a text array field
function isTextArrayField(field: WexlerStandardFieldType | 'custom' | null): boolean {
  return field === 'emailTo' || field === 'issues';
}

// Auto-generate camelCase key from field name
function generateCustomFieldKey(fieldName: string): string {
  return fieldName
    .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special chars
    .split(/\s+/)
    .map((word, index) =>
      index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join('');
}

export function FieldMappings() {
  const { hasPermission } = useHasPermission();
  const { isAuthenticated, isLoading: isAuthLoading, isConnecting, connectRelativity } = useRelativityAuth();
  const [selectedWorkspace, setSelectedWorkspace] = useState<RelativityWorkspaceType | null>(null);
  const [selectedField, setSelectedField] = useState<RelativityFieldType | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMappedOnly, setShowMappedOnly] = useState(false);
  const [localMappings, setLocalMappings] = useState<FieldMappings>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Type validation state
  const [fieldValidations, setFieldValidations] = useState<Record<string, TypeValidationResult>>({});

  const { data: workspaces } = useGetRelativityWorkspaces({
    page: 1,
    pageSize: 100,
    search: '',
    enabled: isAuthenticated,
  });

  const { data: fields, isLoading: fieldsLoading } = useGetRelativityWorkspaceFields({
    workspaceId: selectedWorkspace?.artifactId || null,
    enabled: !!selectedWorkspace && isAuthenticated,
  });

  const { data: existingMapping } = useGetRelativityFieldMapping({
    workspaceId: selectedWorkspace?.artifactId || null,
    enabled: !!selectedWorkspace && isAuthenticated,
  });

  const updateMappingMutation = useUpdateRelativityFieldMapping();

  // Initialize local mappings when existing mapping loads
  useEffect(() => {
    if (existingMapping) {
      setLocalMappings(existingMapping.fieldMappings);
      // Validate all existing mappings
      const validations: Record<string, TypeValidationResult> = {};
      Object.entries(existingMapping.fieldMappings).forEach(([artifactId, config]) => {
        validations[artifactId] = validateTypeCompatibility(config.relativityFieldType, config.wexlerField);
      });
      setFieldValidations(validations);
      setHasUnsavedChanges(false);
    } else if (selectedWorkspace) {
      setLocalMappings({});
      setFieldValidations({});
      setHasUnsavedChanges(false);
    }
  }, [existingMapping, selectedWorkspace]);

  // Filtered fields based on search and mapping status
  const filteredFields = useMemo(() => {
    if (!fields) return [];

    let result = fields.fields;

    // Filter by search query
    if (searchQuery.trim()) {
      result = result.filter((field) => field.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }

    // Filter by mapped status
    if (showMappedOnly) {
      result = result.filter((field) => !!localMappings[field.artifactId.toString()]);
    }

    return result;
  }, [fields, searchQuery, showMappedOnly, localMappings]);

  // Get fields that are already mapped to standard fields
  const mappedStandardFields = useMemo(() => {
    const mapped = new Set<string>();
    Object.values(localMappings).forEach((config) => {
      if (config.wexlerField && config.wexlerField !== 'custom') {
        mapped.add(config.wexlerField);
      }
    });
    return mapped;
  }, [localMappings]);

  // Create options for react-select
  const wexlerFieldOptions = useMemo((): FilterOption[] => {
    if (!selectedField) {
      return [{ label: 'Custom Field', value: 'custom' }];
    }

    const options: FilterOption[] = [{ label: 'Custom Field', value: 'custom' }];

    WEXLER_STANDARD_FIELDS.forEach((field) => {
      const isAlreadyMapped =
        mappedStandardFields.has(field) &&
        localMappings[selectedField?.artifactId.toString() || '']?.wexlerField !== field;

      // Check type compatibility
      const validation = validateTypeCompatibility(selectedField.fieldType || '', field);
      const isIncompatible = validation.compatible === TypeCompatibility.Error;

      const suffix = isIncompatible ? ' (incompatible)' : isAlreadyMapped ? ' (already mapped)' : '';

      options.push({
        label: fieldToDisplayName(field) + suffix,
        value: field,
        isDisabled: isAlreadyMapped || isIncompatible,
      });
    });

    return options;
  }, [mappedStandardFields, localMappings, selectedField]);

  // Check if there are any validation errors (blocks save)
  const hasValidationErrors = useMemo(() => {
    return Object.values(fieldValidations).some((validation) => validation.compatible === TypeCompatibility.Error);
  }, [fieldValidations]);

  // Count errors and warnings
  const validationCounts = useMemo(() => {
    let errors = 0;
    let warnings = 0;
    Object.values(fieldValidations).forEach((validation) => {
      if (validation.compatible === TypeCompatibility.Error) errors++;
      if (validation.compatible === TypeCompatibility.Warning) warnings++;
    });
    return { errors, warnings };
  }, [fieldValidations]);

  // Handle field mapping update
  const updateFieldMapping = (
    artifactId: string,
    wexlerField: WexlerStandardFieldType | 'custom' | null,
    field: RelativityFieldType,
  ) => {
    const customFieldKey = wexlerField === 'custom' ? generateCustomFieldKey(field.name) : undefined;

    // Set default separator for text array fields
    const existingSeparator = localMappings[artifactId]?.arraySeparator;
    const arraySeparator = isTextArrayField(wexlerField) ? existingSeparator || ';' : undefined;

    // Validate type compatibility
    const validation = validateTypeCompatibility(field.fieldType || '', wexlerField);

    setLocalMappings((prev) => ({
      ...prev,
      [artifactId]: {
        relativityFieldName: field.name,
        relativityFieldType: field.fieldType || '',
        wexlerField,
        customFieldKey,
        arraySeparator,
        enabled: true, // Always enabled
      },
    }));

    // Update validation results
    setFieldValidations((prev) => ({
      ...prev,
      [artifactId]: validation,
    }));

    setHasUnsavedChanges(true);
  };

  // Update array separator
  const updateArraySeparator = (artifactId: string, separator: string) => {
    setLocalMappings((prev) => ({
      ...prev,
      [artifactId]: {
        ...prev[artifactId],
        arraySeparator: separator,
      },
    }));
    setHasUnsavedChanges(true);
  };

  // Clear field mapping
  const clearFieldMapping = (artifactId: string) => {
    setLocalMappings((prev) => {
      const newMappings = { ...prev };
      delete newMappings[artifactId];
      return newMappings;
    });
    setFieldValidations((prev) => {
      const newValidations = { ...prev };
      delete newValidations[artifactId];
      return newValidations;
    });
    setHasUnsavedChanges(true);
  };

  // Save mappings
  const handleSave = async () => {
    if (!selectedWorkspace) return;

    try {
      await updateMappingMutation.mutateAsync({
        workspaceId: selectedWorkspace.artifactId,
        workspaceName: selectedWorkspace.name,
        fieldMappings: localMappings,
      });
      toast.success('Field mappings saved successfully');
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving field mappings:', error);
      toast.error('Failed to save field mappings');
    }
  };

  // Discard changes
  const handleDiscard = () => {
    if (existingMapping) {
      setLocalMappings(existingMapping.fieldMappings);
      // Re-validate existing mappings
      const validations: Record<string, TypeValidationResult> = {};
      Object.entries(existingMapping.fieldMappings).forEach(([artifactId, config]) => {
        validations[artifactId] = validateTypeCompatibility(config.relativityFieldType, config.wexlerField);
      });
      setFieldValidations(validations);
    } else {
      setLocalMappings({});
      setFieldValidations({});
    }
    setHasUnsavedChanges(false);
    toast.info('Changes discarded');
  };

  if (!hasPermission(Permission.IntegrationsManage)) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <p className="text-sm text-gray-500">
          You do not have permission to manage field mappings. Please contact your administrator.
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col space-y-6">
      {/* Authentication Check */}
      {isAuthLoading ? (
        <div className="flex items-center justify-center rounded-lg border border-gray-200 bg-white py-12 shadow-sm">
          <Icon className="lucide--loader-2 h-6 w-6 animate-spin text-gray-400" />
          <p className="ml-3 text-sm text-gray-500">Checking authentication status...</p>
        </div>
      ) : !isAuthenticated ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
          <div className="text-center">
            <Icon className="lucide--lock mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 font-serif text-lg font-semibold text-gray-900">Relativity Authentication Required</h3>
            <p className="mt-2 text-sm text-gray-600">
              To configure field mappings, you need to authenticate with your Relativity account. This allows you to
              view available fields in your workspaces.
            </p>
            <Button variant="primary" onClick={connectRelativity} disabled={isConnecting} className="mt-6">
              {isConnecting ? (
                <>
                  <Icon className="lucide--loader-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <Icon className="lucide--lock-open" />
                  Connect to Relativity
                </>
              )}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col space-y-6">
          {/* Workspace Selector */}
          <div className="flex-none space-y-2">
            <Label htmlFor="workspace">Select Workspace</Label>
            <ShadcnSelect
              value={selectedWorkspace?.artifactId.toString()}
              onValueChange={(value) => {
                const workspace = workspaces?.workspaces.find((w) => w.artifactId.toString() === value);
                if (workspace) {
                  setSelectedWorkspace(workspace);
                  setSelectedField(null);
                  setSearchQuery('');
                }
              }}
            >
              <SelectTrigger className="w-full md:w-96">
                <SelectValue placeholder="Choose a workspace..." />
              </SelectTrigger>
              <SelectContent>
                {workspaces?.workspaces.map((workspace) => (
                  <SelectItem key={workspace.artifactId} value={workspace.artifactId.toString()}>
                    {workspace.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </ShadcnSelect>
          </div>

          {/* Validation Summary Banner */}
          {selectedWorkspace && (validationCounts.errors > 0 || validationCounts.warnings > 0) && (
            <div
              className={`flex-none rounded-md border p-4 ${
                validationCounts.errors > 0 ? 'border-red-300 bg-red-50' : 'border-yellow-300 bg-yellow-50'
              }`}
            >
              <div className="flex items-start gap-3">
                <Icon
                  className={`mt-0.5 h-5 w-5 flex-shrink-0 ${
                    validationCounts.errors > 0
                      ? 'lucide--x-circle text-red-600'
                      : 'lucide--alert-triangle text-yellow-600'
                  }`}
                />
                <div className="flex-1">
                  <h4
                    className={`text-sm font-semibold ${
                      validationCounts.errors > 0 ? 'text-red-900' : 'text-yellow-900'
                    }`}
                  >
                    {validationCounts.errors > 0 ? 'Type Compatibility Errors' : 'Type Conversion Warnings'}
                  </h4>
                  <p className={`mt-1 text-sm ${validationCounts.errors > 0 ? 'text-red-700' : 'text-yellow-700'}`}>
                    {validationCounts.errors > 0 ? (
                      <>
                        {validationCounts.errors} field{validationCounts.errors > 1 ? 's have' : ' has'} incompatible
                        type mappings that must be fixed before saving.
                      </>
                    ) : (
                      <>
                        {validationCounts.warnings} field{validationCounts.warnings > 1 ? 's have' : ' has'} type
                        conversion warnings. Review these mappings to ensure they meet your requirements.
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Two-panel layout */}
          {selectedWorkspace && (
            <div className="flex min-h-0 flex-1 flex-col gap-6 lg:flex-row">
              {/* Left Panel - Field List */}
              <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:w-2/5">
                <div className="flex min-h-0 flex-1 flex-col rounded-lg border border-gray-200 bg-white shadow-sm">
                  <div className="flex-none border-b border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-gray-900">Relativity Fields</h3>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="show-mapped-only"
                          checked={showMappedOnly}
                          onCheckedChange={(checked) => setShowMappedOnly(checked === true)}
                        />
                        <label
                          htmlFor="show-mapped-only"
                          className="cursor-pointer text-sm text-gray-600 hover:text-gray-900"
                        >
                          Show Mapped Only
                        </label>
                      </div>
                    </div>
                    <div className="mt-3">
                      <Input
                        placeholder="Search fields..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full"
                      />
                    </div>
                  </div>
                  <div className="min-h-0 flex-1 overflow-y-auto p-2">
                    {fieldsLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Icon className="lucide--loader-2 h-6 w-6 animate-spin text-gray-400" />
                      </div>
                    ) : filteredFields.length === 0 ? (
                      <div className="py-12 text-center">
                        <p className="text-sm text-gray-500">No fields found</p>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {filteredFields.map((field) => {
                          const artifactId = field.artifactId.toString();
                          const isMapped = !!localMappings[artifactId];
                          const isSelected = selectedField?.artifactId === field.artifactId;
                          const validation = fieldValidations[artifactId];

                          return (
                            <button
                              key={field.artifactId}
                              onClick={() => setSelectedField(field)}
                              className={`w-full rounded-md p-3 text-left transition-colors ${
                                isSelected
                                  ? 'bg-teal-50 ring-1 ring-teal-500'
                                  : validation?.compatible === TypeCompatibility.Error
                                    ? 'border-l-2 border-red-400'
                                    : validation?.compatible === TypeCompatibility.Warning
                                      ? 'border-l-2 border-yellow-400'
                                      : validation?.compatible === TypeCompatibility.Info
                                        ? 'border-l-2 border-blue-400'
                                        : 'hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-sm font-medium text-gray-900">{field.name}</p>
                                  <p className="text-xs text-gray-500">{field.fieldType}</p>
                                </div>
                                <div className="ml-2 flex flex-shrink-0 items-center gap-1">
                                  {validation && (
                                    <Icon className={`h-4 w-4 ${getValidationIconClass(validation.compatible)}`} />
                                  )}
                                  {isMapped && !validation && <div className="h-2 w-2 rounded-full bg-teal-500"></div>}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Panel - Field Mapping */}
              <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:w-3/5">
                <div className="flex min-h-0 flex-1 flex-col rounded-lg border border-gray-200 bg-white shadow-sm">
                  <div className="flex-none border-b border-gray-200 p-4">
                    <h3 className="font-medium text-gray-900">Field Mapping</h3>
                  </div>
                  <div className="min-h-0 flex-1 overflow-y-auto p-6">
                    {!selectedField ? (
                      <div className="flex h-full flex-col items-center justify-center text-center">
                        <Icon className="lucide--mouse-pointer-click h-12 w-12 text-gray-300" />
                        <p className="mt-4 text-sm text-gray-500">
                          Select a field from the list to configure its mapping
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        <div>
                          <Label className="text-base font-semibold text-gray-900">{selectedField.name}</Label>
                          <p className="mt-1 text-sm text-gray-500">
                            Type: <span className="font-medium">{selectedField.fieldType}</span>
                          </p>
                        </div>

                        {/* Check if this is a Single/Multiple Object field */}
                        {(() => {
                          const isObjectField =
                            selectedField.fieldType === 'Single Object' ||
                            selectedField.fieldType === 'Multiple Object';

                          if (isObjectField) {
                            return (
                              <>
                                <div className="space-y-2">
                                  <Label htmlFor="wexler-field" className="text-gray-400">
                                    Map to Wexler Field
                                  </Label>
                                  <Select
                                    placeholder="Cannot map object fields"
                                    options={[]}
                                    selectedOption={null}
                                    onChange={() => {}}
                                    className="w-full opacity-50"
                                  />

                                  {/* Always show error message for object fields */}
                                  <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-3">
                                    <Icon className="lucide--x-circle mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
                                    <p className="text-sm text-red-700">
                                      Single Object and Multiple Object fields cannot be mapped. These fields reference
                                      other Relativity objects.
                                    </p>
                                  </div>
                                </div>
                              </>
                            );
                          }

                          // Normal field mapping UI
                          return (
                            <div className="space-y-2">
                              <Label htmlFor="wexler-field">Map to Wexler Field</Label>
                              <Select
                                placeholder="Search and select a Wexler field..."
                                options={wexlerFieldOptions}
                                selectedOption={
                                  wexlerFieldOptions.find(
                                    (opt) =>
                                      opt.value === localMappings[selectedField.artifactId.toString()]?.wexlerField,
                                  ) || null
                                }
                                onChange={(newValue) => {
                                  if (newValue?.value) {
                                    updateFieldMapping(
                                      selectedField.artifactId.toString(),
                                      newValue.value as WexlerStandardFieldType | 'custom',
                                      selectedField,
                                    );
                                  }
                                }}
                                className="w-full"
                              />

                              {/* Validation Message */}
                              {localMappings[selectedField.artifactId.toString()] &&
                                fieldValidations[selectedField.artifactId.toString()] &&
                                fieldValidations[selectedField.artifactId.toString()].message && (
                                  <div
                                    className={`flex items-start gap-2 rounded-md border p-3 ${getValidationBorderClass(
                                      fieldValidations[selectedField.artifactId.toString()].compatible,
                                    )} ${getValidationBackgroundClass(
                                      fieldValidations[selectedField.artifactId.toString()].compatible,
                                    )}`}
                                  >
                                    <Icon
                                      className={`mt-0.5 h-4 w-4 flex-shrink-0 ${getValidationIconClass(
                                        fieldValidations[selectedField.artifactId.toString()].compatible,
                                      )}`}
                                    />
                                    <p
                                      className={`text-sm ${getValidationTextClass(
                                        fieldValidations[selectedField.artifactId.toString()].compatible,
                                      )}`}
                                    >
                                      {fieldValidations[selectedField.artifactId.toString()].message}
                                    </p>
                                  </div>
                                )}
                            </div>
                          );
                        })()}

                        {localMappings[selectedField.artifactId.toString()]?.wexlerField === 'custom' && (
                          <div className="space-y-2">
                            <Label>Custom Field Key</Label>
                            <Input
                              value={localMappings[selectedField.artifactId.toString()]?.customFieldKey || ''}
                              readOnly
                              className="bg-gray-50"
                            />
                            <p className="text-xs text-gray-500">
                              This field will be stored in the custom fields JSON object with this key
                            </p>
                          </div>
                        )}

                        {/* Array Separator Input for text array fields */}
                        {localMappings[selectedField.artifactId.toString()] &&
                          isTextArrayField(localMappings[selectedField.artifactId.toString()]?.wexlerField) && (
                            <div className="space-y-2">
                              <Label htmlFor="array-separator">Array Separator</Label>
                              <Input
                                id="array-separator"
                                value={localMappings[selectedField.artifactId.toString()]?.arraySeparator || ';'}
                                onChange={(e) =>
                                  updateArraySeparator(selectedField.artifactId.toString(), e.target.value)
                                }
                                placeholder=";"
                                maxLength={5}
                                className="w-32"
                              />
                              <p className="text-xs text-gray-500">
                                Values in this field will be split by this separator. For example, with semicolon
                                separator: "value1; value2; value3" becomes ["value1", "value2", "value3"]
                              </p>
                            </div>
                          )}

                        {localMappings[selectedField.artifactId.toString()] && (
                          <Button
                            variant="outline"
                            onClick={() => clearFieldMapping(selectedField.artifactId.toString())}
                            className="w-full"
                          >
                            <Icon className="lucide--x" />
                            Clear Mapping
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Save/Discard Actions */}
          {selectedWorkspace && (
            <div className="flex flex-none items-center justify-between border-t border-gray-200 pt-4 pb-6">
              <div className="text-sm text-gray-500">
                {hasUnsavedChanges ? (
                  <span className="inline-flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-yellow-500"></div>
                    Unsaved changes
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    All changes saved
                  </span>
                )}
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={handleDiscard} disabled={!hasUnsavedChanges}>
                  Discard Changes
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges || hasValidationErrors || updateMappingMutation.isLoading}
                  title={
                    hasValidationErrors
                      ? `Cannot save: ${validationCounts.errors} field(s) have incompatible type mappings`
                      : ''
                  }
                >
                  <Icon className="lucide--save" />
                  {updateMappingMutation.isLoading ? 'Saving...' : 'Save Mappings'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
